import { useEffect, useState, useCallback } from "react";
import { X, Loader2, Monitor, ShieldAlert, CheckSquare, Square, Clock, Zap } from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";

export interface OSPatchableAsset {
    asset_id: number;
    update_id: string;
    name: string;
    title: string;
    current_version: string | null;
    new_version: string | null;
    severity: string;
    reboot_required: boolean;
    os_type: string;
    pkg_manager: string;
    kb_ids: string;
    cve_ids: string;
    asset_hostname: string;
    ip_address: string;
    mac_address: string;
    os_name: string;
    os_version: string;
}

interface OSPatchableAssetsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    clientId: any;
    updateName: string; // e.g. "KB5082089"
    updateTitle: string; // e.g. "2026-04 Servicing Stack Update..."
    search?: string;
}

export default function OSPatchableAssetsPanel({
    isOpen,
    onClose,
    clientId,
    updateName,
    search,
}: OSPatchableAssetsPanelProps) {
    const [assets, setAssets] = useState<OSPatchableAsset[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);


    // Reboot Policy State
    const [rebootPolicy, setRebootPolicy] = useState<"immediate" | "scheduled">("immediate");
    const [rebootTime, setRebootTime] = useState<string>("");

    const fetchAssets = useCallback(async () => {
        if (!isOpen || !clientId || !updateName) return;

        setLoading(true);
        try {
            const queryObj: any = {};
            if (search) {
                queryObj.$or = [
                    { asset_hostname: { $ilike: `%${search}%` } },
                    { ip_address: { $ilike: `%${search}%` } },
                    { mac_address: { $ilike: `%${search}%` } },
                    { os_name: { $ilike: `%${search}%` } },
                    { name: { $ilike: `%${search}%` } },
                    { title: { $ilike: `%${search}%` } }
                ];
            }

            const params: any = {
                client_id: clientId,
                name: updateName,
                search: search || undefined,
                query: Object.keys(queryObj).length > 0 ? JSON.stringify(queryObj) : undefined
            };

            const res = await baseapi.get("/patch/os/pending_assets", { params });

            if (res.data.status) {
                setAssets(res.data.data ?? []);
            } else {
                showError(res.data.message || "Failed to load OS patchable assets");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [isOpen, clientId, updateName, search]);

    useEffect(() => {
        fetchAssets();
        // Reset selections and settings when opened with a new update
        setSelectedAssetIds([]);
        setRebootPolicy("immediate");
        setRebootTime("");
    }, [fetchAssets]);

    const handleSelectAll = () => {
        if (selectedAssetIds.length === assets.length) {
            setSelectedAssetIds([]);
        } else {
            setSelectedAssetIds(assets.map(a => a.asset_id));
        }
    };

    const handleSelectOne = (id: number) => {
        setSelectedAssetIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const getNowLocal = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset()); // fix timezone
        return now.toISOString().slice(0, 16);
    };

    const handleDateChange = (value: string) => {
        const selected = new Date(value);
        const now = new Date();

        if (selected.getTime() < now.getTime()) {
            showError("You cannot select a past date or time");
            setRebootTime(""); // reset input
            return;
        }

        setRebootTime(value);
    }

    const handlePatchSelected = async () => {
        if (rebootPolicy === "scheduled" && !rebootTime) {
            showError("Please select a valid reboot time for the scheduled policy.");
            return;
        }

        try {
            const payload: any = {
                asset_ids: selectedAssetIds,
                update_ids: [updateName],
                reboot_policy: rebootPolicy,
                client_id: clientId
            };

            if (rebootPolicy === "scheduled") {
                payload.reboot_time = new Date(rebootTime).toISOString();
            }

            const response = await baseapi.post("/patch/os/patch_job", payload);

            if (response.data.status) {
                showSuccess(response.data.message || "OS patch job created successfully");
                onClose();
            } else {
                showError(response.data.message || "Failed to create OS patch job");
            }
        } catch (e: any) {
            showError(e.message);
        }
    };

    const getOsIcon = (os?: string) => {
        if (!os) return null;
        const value = os.toLowerCase();

        if (value.includes("windows")) return "/windows.png";
        if (value.includes("mac") || value.includes("darwin") || value.includes("os x")) return "/mac.png";
        if (value.includes("linux") || value.includes("ubuntu") || value.includes("debian") || value.includes("centos"))
            return "/linux.svg";

        return null;
    };

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/10 backdrop-blur-sm z-[90] transition-opacity"
                    onClick={onClose}
                />
            )}

            {/* Sliding Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-[35%] min-w-[340px] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 shrink-0 bg-gray-50 dark:bg-gray-900/50">
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                            Patch OS Assets
                        </h2>
                        {/* <span className="text-[10px] text-gray-400 mt-1 font-mono line-clamp-2 max-w-[280px]" title={updateTitle}>
                            {updateTitle}
                        </span> */}
                        <span className="text-[9px] text-orange-600 dark:text-orange-400 font-bold mt-1 uppercase tracking-widest">
                            {updateName}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors self-start"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-4 bg-gray-50/40 dark:bg-transparent">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 opacity-60">
                            <Loader2 className="w-6 h-6 animate-spin text-orange-500 dark:text-orange-400" />
                            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                Loading assets...
                            </span>
                        </div>
                    ) : assets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 opacity-40">
                            <Monitor className="w-8 h-8 text-gray-400" />
                            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                No assets found
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between px-1 mb-2">
                                <button
                                    onClick={handleSelectAll}
                                    className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                                >
                                    {selectedAssetIds.length === assets.length ? (
                                        <CheckSquare className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                    ) : (
                                        <Square className="w-4 h-4" />
                                    )}
                                    Select All
                                </button>
                                <span className="text-[10px] text-gray-500 font-bold">
                                    {assets.length} ASSET{assets.length !== 1 ? 'S' : ''}
                                </span>
                            </div>

                            {assets.map((asset) => {
                                const isSelected = selectedAssetIds.includes(asset.asset_id);
                                return (
                                    <div
                                        key={asset.asset_id}
                                        onClick={() => handleSelectOne(asset.asset_id)}
                                        className={`flex flex-col p-3 rounded-xl border transition-all cursor-pointer ${isSelected
                                            ? "bg-orange-500/10 border-orange-500/30"
                                            : "bg-white dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/80 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm"
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5">
                                                {isSelected ? (
                                                    <CheckSquare className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                                ) : (
                                                    <Square className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                                                        {asset.asset_hostname}
                                                    </h3>
                                                    <div className="flex items-end gap-0.5">
                                                        <span className="text-[9px] font-mono text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-purple-200 dark:border-gray-800">
                                                            IP: {asset.ip_address}
                                                        </span>
                                                        <span className="text-[9px] font-mono text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-cyan-200 dark:border-gray-800 mt-1">
                                                            MAC: {asset.mac_address}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-4 gap-2 mt-2">
                                                    {/* Current */}
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest mb-0.5">
                                                            Current
                                                        </span>
                                                        <span className="text-[10px] font-mono text-orange-600 dark:text-orange-300 font-semibold">
                                                            {asset.current_version || "-"}
                                                        </span>
                                                    </div>

                                                    {/* New Version */}
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest mb-0.5">
                                                            New
                                                        </span>
                                                        <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-300 font-semibold">
                                                            {asset.new_version || "-"}
                                                        </span>
                                                    </div>

                                                    {/* OS Name → spans 2 columns */}
                                                    <div className="flex flex-col col-span-2">
                                                        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest mb-0.5">
                                                            OS Name
                                                        </span>

                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            {getOsIcon(asset.os_type) && (
                                                                <img
                                                                    src={getOsIcon(asset.os_type)!}
                                                                    alt="os"
                                                                    className="w-3.5 h-3.5 flex-shrink-0"
                                                                />
                                                            )}

                                                            <span className="text-[10px] font-mono font-semibold text-lime-700 dark:text-lime-300 truncate" title={asset.os_name}>
                                                                {asset.os_name || "-"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer Configurations & Actions */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0 flex flex-col gap-4 shadow-sm">

                    {/* Reboot Policy Selection */}
                    <div className="flex flex-col gap-2">
                        <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-widest">
                            Reboot Policy
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setRebootPolicy("immediate")}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all ${rebootPolicy === "immediate"
                                    ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-400"
                                    : "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                                    }`}
                            >
                                <Zap size={14} /> Immediate
                            </button>
                            <button
                                onClick={() => setRebootPolicy("scheduled")}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all ${rebootPolicy === "scheduled"
                                    ? "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:border-purple-500/30 dark:text-purple-400"
                                    : "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                                    }`}
                            >
                                <Clock size={14} /> Scheduled
                            </button>
                        </div>
                    </div>

                    {/* DateTime Picker for Scheduled Reboot */}
                    {rebootPolicy === "scheduled" && (
                        <div className="flex flex-col gap-1.5 animate-fade-in">
                            <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-widest">
                                Scheduled Time
                            </span>
                            <input
                                type="datetime-local"
                                value={rebootTime}
                                min={getNowLocal()}
                                onChange={(e) => handleDateChange(e.target.value)}
                                className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-200 text-xs rounded-lg block w-full p-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                            />
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        onClick={handlePatchSelected}
                        disabled={selectedAssetIds.length === 0 || loading}
                        className="w-full mt-2 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:border disabled:border-gray-200 dark:disabled:border-gray-700 text-white text-[11px] font-bold uppercase tracking-widest rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm"
                    >
                        <ShieldAlert className="w-3.5 h-3.5" />
                        Patch Selected ({selectedAssetIds.length})
                    </button>
                </div>
            </div>
        </>
    );
}